import React, { useState, useEffect, useCallback, useRef } from "react";
import { useCss } from "../context/CssContext";
import { useStatic } from "../context/StaticContext";
import config from "../config";

const BannerCarousel = () => {
  const { bannerImages: cssBanners } = useCss();
  const { bonusBanners, bonusBannerVisible } = useStatic();
  
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Müşteri "Banner Ekleme" ve "CSS Düzenleyici" bannerlarını birleştiriyor
  let combinedBanners = [];
  
  if (bonusBanners && bonusBanners.length > 0) {
    const activeBonusBanners = bonusBanners
      .filter((b) => b.status === 'active')
      .sort((a, b) => a.order - b.order)
      .map((b) => {
        let imgUrl = b.image || '';
        if (imgUrl && !imgUrl.startsWith('http') && !imgUrl.startsWith('data:')) {
          const prefix = config.imgUrl && config.imgUrl.endsWith('/') ? config.imgUrl : `${config.imgUrl || ''}/`;
          const cleanedImg = imgUrl.startsWith('/') ? imgUrl.slice(1) : imgUrl;
          imgUrl = `${prefix}${cleanedImg}`;
        }
        return {
          img: imgUrl,
          target_url: b.target_url || b.button_url || b.url || '',
          button_text: b.button_text || '',
          button_color: b.button_color || '#000000',
          button_bg_color: b.button_bg_color || '#FFC107',
        };
      });
    combinedBanners = [...combinedBanners, ...activeBonusBanners];
  }
  
  if (cssBanners && cssBanners.length > 0) {
    const formattedCssBanners = cssBanners.map((item) => {
      if (typeof item === 'string') {
        return {
          img: item,
          target_url: '',
          button_text: '',
          button_color: '#000000',
          button_bg_color: '#FFC107',
        };
      }
      return item;
    });
    combinedBanners = [...combinedBanners, ...formattedCssBanners];
  }

  const bannerImages = combinedBanners;
  const bannerEnabled = bonusBannerVisible;

  const totalSlides = bannerImages ? bannerImages.length : 0;
  const isVisible = bannerEnabled && totalSlides > 0;

  // Otomatik geçiş zamanlayıcısı
  const startAutoPlay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (totalSlides <= 1) return;
    timerRef.current = setInterval(() => {
      setIsTransitioning(true);
      setActiveIndex((prev) => (prev + 1) % totalSlides);
      setTimeout(() => setIsTransitioning(false), 500);
    }, 4000);
  }, [totalSlides]);

  useEffect(() => {
    if (!isVisible) return;
    startAutoPlay();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startAutoPlay, isVisible]);

  // activeIndex totalSlides'ı aşmasın
  useEffect(() => {
    if (activeIndex >= totalSlides && totalSlides > 0) {
      setActiveIndex(0);
    }
  }, [totalSlides, activeIndex]);

  // Banner gösterilmemesi gereken durumlar
  if (!isVisible) {
    return null;
  }

  const goToSlide = (index) => {
    if (index === activeIndex || isTransitioning) return;
    setIsTransitioning(true);
    setActiveIndex(index);
    startAutoPlay();
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const goNext = () => {
    if (isTransitioning) return;
    goToSlide((activeIndex + 1) % totalSlides);
  };

  const goPrev = () => {
    if (isTransitioning) return;
    goToSlide((activeIndex - 1 + totalSlides) % totalSlides);
  };

  // Touch desteği
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
  };

  return (
    <div
      className="banner-carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slider Track */}
      <div
        className="banner-carousel-track"
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {bannerImages.map((banner, idx) => {
          const item = typeof banner === 'string' ? { img: banner } : banner;
          const hasLink = Boolean(item.target_url && item.target_url.trim());
          const hasButtonText = Boolean(item.button_text && item.button_text.trim());

          const handleBannerClick = () => {
            if (hasLink) {
              window.open(item.target_url, '_blank', 'noopener,noreferrer');
            }
          };

          return (
            <div
              key={idx}
              className={`banner-carousel-slide${hasLink ? ' clickable' : ''}`}
              onClick={handleBannerClick}
            >
              <img src={item.img} alt={`Banner ${idx + 1}`} className="banner-carousel-img" />

              {(hasButtonText || hasLink) && (
                <div className="banner-carousel-overlay">
                  {hasButtonText && (
                    <button
                      className="banner-carousel-button"
                      style={{
                        backgroundColor: item.button_bg_color || '#FFC107',
                        color: item.button_color || '#000000',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasLink) {
                          window.open(item.target_url, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      <svg
                        className="banner-carousel-button-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                      <span>{item.button_text}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      {totalSlides > 1 && (
        <>
          <button
            className="banner-carousel-arrow banner-carousel-arrow--prev"
            onClick={goPrev}
            aria-label="Önceki"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            className="banner-carousel-arrow banner-carousel-arrow--next"
            onClick={goNext}
            aria-label="Sonraki"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Dots */}
      {totalSlides > 1 && (
        <div className="banner-carousel-dots">
          {bannerImages.map((_, idx) => (
            <button
              key={idx}
              className={`banner-carousel-dot${idx === activeIndex ? " active" : ""}`}
              onClick={() => goToSlide(idx)}
              aria-label={`Banner ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerCarousel;
